import React, { useEffect, useState, useRef, useCallback } from "react";
import MainLayout from "../../Layout/mainLayout";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addNewCity,
  viewCountry,
  viewState,
  viewAllCity,
} from "../../Redux/Actions/settingsActions";
import AddImage from "../../Global Functions/addImage";
import ImageShowerModalGlobal from "../../Global Functions/imageShowerModalGlobal";
import toast from "react-hot-toast";
import { GoogleMap, useLoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import "./addCity.css";

const libraries = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "380px",
  borderRadius: "10px",
};

const defaultCenter = { lat: 28.6139, lng: 77.209 };

const AddCity = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { country } = useSelector((state) => state.country || {});
  const { stateData } = useSelector((state) => state.states || {});
  const { loading = false, citiesList } = useSelector((state) => state.city || {});

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [markerPosition, setMarkerPosition] = useState(null);

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const hasFetchedCities = useRef(false);

  const [cityData, setCityData] = useState({
    city_name: "",
    city_name_withoutspace: "",
    google_city_name: "",
    state: "",
    country: "",
    image: "",
    description: "",
    is_vr_home_visit_available: false,
    is_popular: false,
    associated_cities: [], // stores { _id, city_name } objects locally
    location: {
      type: "Point",
      coordinates: [0, 0], // [lng, lat]
    },
  });

  // ── Fetch all cities for associated cities dropdown ──
  useEffect(() => {
    if (!citiesList?.data?.length && !hasFetchedCities.current) {
      dispatch(viewAllCity());
      hasFetchedCities.current = true;
    }
  }, [dispatch, citiesList?.data?.length]);

  useEffect(() => {
    if (id) dispatch(viewState(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (Array.isArray(stateData?.data) && stateData.data.length > 0) {
      const state = stateData.data[0];
      setCityData((prev) => ({ ...prev, state: state._id, country: state.country }));
      dispatch(viewCountry(state.country._id));
    }
  }, [stateData]);

  useEffect(() => {
    if (!cityData.country && Array.isArray(country?.data) && country.data.length > 0) {
      setCityData((prev) => ({ ...prev, country: country.data[0]._id }));
    }
  }, [country, cityData.country]);

  // Auto-fill city_name_withoutspace
  useEffect(() => {
    setCityData((prev) => ({
      ...prev,
      city_name_withoutspace: prev.city_name.replace(/\s+/g, ""),
    }));
  }, [cityData.city_name]);

  // ── Map handlers ──
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onMapClick = useCallback((event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    setMarkerPosition({ lat, lng });
    setCityData((prev) => ({
      ...prev,
      location: { type: "Point", coordinates: [lng, lat] },
    }));
    setErrors((prev) => ({ ...prev, location: "" }));

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setCityData((prev) => ({
          ...prev,
          google_city_name: results[0].formatted_address,
        }));
      }
    });
  }, []);

  const onAutocompleteLoad = (autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        setMarkerPosition({ lat, lng });
        setMapCenter({ lat, lng });

        setCityData((prev) => ({
          ...prev,
          google_city_name: place.formatted_address || "",
          location: { type: "Point", coordinates: [lng, lat] },
        }));

        setErrors((prev) => ({ ...prev, location: "" }));
      }
    }
  };

  // ── Validation ──
  const validate = () => {
    const newErrors = {};

    if (!cityData.city_name.trim()) {
      newErrors.city_name = "City name is required";
    } else if (cityData.city_name.trim().length < 2) {
      newErrors.city_name = "City name must be at least 2 characters";
    } else if (cityData.city_name.trim().length > 100) {
      newErrors.city_name = "City name must be under 100 characters";
    }

    if (!cityData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (cityData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (cityData.description.trim().length > 500) {
      newErrors.description = "Description must be under 500 characters";
    }

    if (!cityData.state) newErrors.state = "State is required";
    if (!cityData.country) newErrors.country = "Country is required";

    if (
      cityData.location.coordinates[0] === 0 &&
      cityData.location.coordinates[1] === 0
    ) {
      newErrors.location = "Please select a location on the map";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    const payload = {
      city_name: cityData.city_name.trim(),
      city_name_withoutspace: cityData.city_name.trim().replace(/\s+/g, ""),
      google_city_name: cityData.google_city_name,
      state: cityData.state,
      country: cityData.country,
      image: cityData.image,
      description: cityData.description.trim(),
      is_vr_home_visit_available: cityData.is_vr_home_visit_available,
      is_popular: cityData.is_popular,
      location: cityData.location,
      associated_cities: cityData.associated_cities.map((c) => c._id), // send only _id array
    };

    try {
      const response = await dispatch(addNewCity(payload));
      if (response?.statusCode === 200) {
        toast.success("City added successfully!");
        navigate(`/city/${cityData.state}`);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to add city");
    }
  };

  const handleImageUpload = async (event) => {
    const uploadedImage = event.target.files[0];
    if (!uploadedImage) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!allowedTypes.includes(uploadedImage.type)) {
      toast.error("Only JPG, PNG, or WEBP images are allowed");
      return;
    }
    if (uploadedImage.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }

    setImagePreview(URL.createObjectURL(uploadedImage));
    const imageUrl = await AddImage(uploadedImage);
    if (imageUrl) {
      setCityData((prev) => ({ ...prev, image: imageUrl }));
    }
  };

  const handleAddChange = (event) => {
    const { name, value } = event.target;
    setCityData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleToggleChange = (fieldName) => {
    setCityData((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  // ── Associated Cities handlers ──
  const handleAssocCitySelect = (e) => {
    const selectedId = e.target.value;
    if (!selectedId) return;

    const alreadyAdded = cityData.associated_cities.some((c) => c._id === selectedId);
    if (alreadyAdded) {
      toast.error("City already added");
      e.target.value = "";
      return;
    }

    const selectedCity = citiesList?.data?.find((c) => c._id === selectedId);
    if (!selectedCity) return;

    setCityData((prev) => ({
      ...prev,
      associated_cities: [
        ...prev.associated_cities,
        { _id: selectedCity._id, city_name: selectedCity.city_name },
      ],
    }));
    e.target.value = ""; // reset dropdown
  };

  const handleRemoveAssocCity = (cityId) => {
    setCityData((prev) => ({
      ...prev,
      associated_cities: prev.associated_cities.filter((c) => c._id !== cityId),
    }));
  };

  const countryName =
    Array.isArray(country?.data)
      ? country.data[0]?.country_name
      : country?.data?.country_name || "N/A";

  const stateName =
    Array.isArray(stateData?.data) && stateData.data.length > 0
      ? stateData.data[0]?.state_name
      : "N/A";

  const hasLocation =
    cityData.location.coordinates[0] !== 0 ||
    cityData.location.coordinates[1] !== 0;

  return (
    <MainLayout>
      <div className="acy-wrapper">
        {/* Header */}
        <div className="acy-header">
          <div className="acy-header-left">
            <div className="acy-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div>
              <h1 className="acy-title">Add City</h1>
              <p className="acy-subtitle">Fill in the details to add a new city</p>
            </div>
          </div>
          <button onClick={() => navigate(-1)} className="acy-view-btn" type="button">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            View City List
          </button>
        </div>

        <form onSubmit={handleSubmit} className="acy-form" noValidate>

          {/* ── Section 01: Location Context ── */}
          <div className="acy-section">
            <div className="acy-section-header">
              <span className="acy-section-badge">01</span>
              <h2 className="acy-section-title">Location Context</h2>
            </div>
            <div className="acy-grid acy-grid-2">
              <div className="acy-field">
                <label className="acy-label">Country</label>
                <input className="acy-input acy-input--readonly" type="text" value={countryName} readOnly disabled />
              </div>
              <div className="acy-field">
                <label className="acy-label">State</label>
                <input className="acy-input acy-input--readonly" type="text" value={stateName} readOnly disabled />
              </div>
            </div>
          </div>

          {/* ── Section 02: City Information ── */}
          <div className="acy-section">
            <div className="acy-section-header">
              <span className="acy-section-badge">02</span>
              <h2 className="acy-section-title">City Information</h2>
            </div>
            <div className="acy-grid acy-grid-2">
              <div className="acy-field">
                <label className="acy-label">City Name <span className="acy-req">*</span></label>
                <input
                  className={`acy-input ${errors.city_name ? "acy-input--error" : ""}`}
                  type="text"
                  name="city_name"
                  value={cityData.city_name}
                  onChange={handleAddChange}
                  placeholder="e.g. New Delhi"
                  disabled={loading}
                  maxLength={100}
                />
                {errors.city_name && (
                  <span className="acy-error-msg">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {errors.city_name}
                  </span>
                )}
                <span className="acy-char-count">{cityData.city_name.length}/100</span>
              </div>

              <div className="acy-field">
                <label className="acy-label">City Name <span className="acy-optional">(without spaces)</span></label>
                <input
                  className="acy-input acy-input--readonly"
                  type="text"
                  value={cityData.city_name_withoutspace}
                  readOnly
                  disabled
                  placeholder="Auto-generated"
                />
                <span className="acy-hint">Auto-filled from city name</span>
              </div>

              <div className="acy-field acy-field--full">
                <label className="acy-label">Description <span className="acy-req">*</span></label>
                <textarea
                  className={`acy-textarea ${errors.description ? "acy-input--error" : ""}`}
                  name="description"
                  value={cityData.description}
                  onChange={handleAddChange}
                  placeholder="Brief description of the city..."
                  disabled={loading}
                  maxLength={500}
                  rows={3}
                />
                {errors.description && (
                  <span className="acy-error-msg">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    {errors.description}
                  </span>
                )}
                <span className="acy-char-count">{cityData.description.length}/500</span>
              </div>
            </div>
          </div>

          {/* ── Section 03: Map & Coordinates ── */}
          <div className="acy-section">
            <div className="acy-section-header">
              <span className="acy-section-badge">03</span>
              <h2 className="acy-section-title">Map & Coordinates</h2>
            </div>

            <div className="acy-field acy-mb-20">
              <label className="acy-label">Google City Name</label>
              <input
                className="acy-input acy-input--readonly"
                type="text"
                value={cityData.google_city_name}
                readOnly
                disabled
                placeholder="Auto-filled when location is selected"
              />
              <span className="acy-hint">Populated automatically from map selection</span>
            </div>

            {loadError && (
              <div className="acy-map-placeholder">
                <div className="acy-map-placeholder-icon">🗺️</div>
                <p className="acy-map-placeholder-title">Map unavailable</p>
                <p className="acy-map-placeholder-desc">Check your Google Maps API key</p>
              </div>
            )}

            {!isLoaded && !loadError && (
              <div className="acy-map-placeholder">
                <div className="acy-map-loading-spinner" />
                <p className="acy-map-placeholder-title">Loading map...</p>
              </div>
            )}

            {isLoaded && !loadError && (
              <div className="acy-map-wrap">
                <Autocomplete onLoad={onAutocompleteLoad} onPlaceChanged={onPlaceChanged}>
                  <input
                    type="text"
                    placeholder="Search for a place..."
                    className="acy-input acy-map-search"
                  />
                </Autocomplete>
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={13}
                  onClick={onMapClick}
                  onLoad={onMapLoad}
                >
                  {markerPosition && <Marker position={markerPosition} />}
                </GoogleMap>
                <p className="acy-map-hint">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  Click on the map or search above to set the city location
                </p>
              </div>
            )}

            {errors.location && (
              <span className="acy-error-msg acy-mt-8">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {errors.location}
              </span>
            )}

            <div className="acy-grid acy-grid-2 acy-mt-16">
              <div className="acy-field">
                <label className="acy-label">Longitude</label>
                <input
                  className="acy-input acy-input--readonly"
                  type="text"
                  value={hasLocation ? cityData.location.coordinates[0] : ""}
                  readOnly
                  disabled
                  placeholder="Auto-filled from map"
                />
              </div>
              <div className="acy-field">
                <label className="acy-label">Latitude</label>
                <input
                  className="acy-input acy-input--readonly"
                  type="text"
                  value={hasLocation ? cityData.location.coordinates[1] : ""}
                  readOnly
                  disabled
                  placeholder="Auto-filled from map"
                />
              </div>
            </div>
          </div>

          {/* ── Section 04: Settings ── */}
          <div className="acy-section">
            <div className="acy-section-header">
              <span className="acy-section-badge">04</span>
              <h2 className="acy-section-title">Settings</h2>
            </div>
            <div className="acy-grid acy-grid-2">
              <div className="acy-toggle-card">
                <div className="acy-toggle-info">
                  <div className="acy-toggle-icon acy-toggle-icon--vr">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </div>
                  <div>
                    <p className="acy-toggle-label">VR Home Visit</p>
                    <p className="acy-toggle-desc">
                      {cityData.is_vr_home_visit_available ? "Available in this city" : "Not available"}
                    </p>
                  </div>
                </div>
                <label className="acy-switch">
                  <input type="checkbox" checked={cityData.is_vr_home_visit_available} onChange={() => handleToggleChange("is_vr_home_visit_available")} disabled={loading} />
                  <span className="acy-switch-track"><span className="acy-switch-thumb" /></span>
                </label>
              </div>

              <div className="acy-toggle-card">
                <div className="acy-toggle-info">
                  <div className="acy-toggle-icon acy-toggle-icon--popular">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </div>
                  <div>
                    <p className="acy-toggle-label">Popular City</p>
                    <p className="acy-toggle-desc">
                      {cityData.is_popular ? "Marked as popular" : "Not marked as popular"}
                    </p>
                  </div>
                </div>
                <label className="acy-switch">
                  <input type="checkbox" checked={cityData.is_popular} onChange={() => handleToggleChange("is_popular")} disabled={loading} />
                  <span className="acy-switch-track"><span className="acy-switch-thumb" /></span>
                </label>
              </div>
            </div>
          </div>

          {/* ── Section 05: Associated Cities ── */}
          <div className="acy-section">
            <div className="acy-section-header">
              <span className="acy-section-badge">05</span>
              <h2 className="acy-section-title">Associated Cities</h2>
            </div>

            <div className="acy-field acy-mb-20">
              <label className="acy-label">Select City</label>
              <div className="acy-assoc-select-wrap">
                <svg className="acy-assoc-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <select
                  className="acy-select"
                  disabled={loading}
                  onChange={handleAssocCitySelect}
                  defaultValue=""
                >
                  <option value="" disabled>
                    {citiesList?.data?.length
                      ? "Choose a city to associate..."
                      : "Loading cities..."}
                  </option>
                  {Array.isArray(citiesList?.data) &&
                    citiesList.data.map((city) => (
                      <option key={city._id} value={city._id}>
                        {city.city_name}
                      </option>
                    ))}
                </select>
                <svg className="acy-select-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
              <span className="acy-hint">Select from the list to add an associated city</span>
            </div>

            {/* Tags */}
            {cityData.associated_cities.length > 0 ? (
              <div className="acy-assoc-tags">
                {cityData.associated_cities.map((city) => (
                  <span key={city._id} className="acy-assoc-tag">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {city.city_name}
                    <button
                      type="button"
                      className="acy-assoc-tag-remove"
                      onClick={() => handleRemoveAssocCity(city._id)}
                      title="Remove"
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <div className="acy-assoc-empty">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <p>No associated cities added yet</p>
              </div>
            )}
          </div>

          {/* ── Section 06: Image ── */}
          <div className="acy-section">
            <div className="acy-section-header">
              <span className="acy-section-badge">06</span>
              <h2 className="acy-section-title">City Image</h2>
            </div>
            <div className="acy-upload-area">
              <input
                id="acy-image-upload"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
                style={{ display: "none" }}
                disabled={loading}
              />
              {imagePreview ? (
                <div className="acy-image-preview">
                  <img src={imagePreview} alt="City preview" className="acy-preview-img" />
                  <label htmlFor="acy-image-upload" className="acy-change-img-btn">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Change Image
                  </label>
                </div>
              ) : (
                <label htmlFor="acy-image-upload" className="acy-upload-label">
                  <div className="acy-upload-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </div>
                  <p className="acy-upload-text">Click to upload city image</p>
                  <p className="acy-upload-hint">JPG, PNG, WEBP — max 5MB</p>
                </label>
              )}
            </div>
            {cityData.image && (
              <ImageShowerModalGlobal
                file={cityData.image}
                state={cityData}
                setState={setCityData}
                keyField="image"
              />
            )}
          </div>

          {/* ── Footer ── */}
          <div className="acy-footer">
            <button type="button" className="acy-btn acy-btn--cancel" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="acy-btn acy-btn--submit" disabled={loading}>
              {loading ? (
                <><span className="acy-spinner" />Saving...</>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Save City
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
};

export default AddCity;